if (!customElements.get('planted-cart')) {
  function setClassesFromAttribute(attr, el) {
    const classes = (el.getAttribute(attr) || '').split(/\s+/)
    el.classList.add(...classes)
  }

  function unsetClassesFromAttribute(attr, el) {
    const classes = (el.getAttribute(attr) || '').split(/\s+/)
    el.classList.remove(...classes)
  }

  function debounce(func, timeout = 500) {
    let timer
    return (...args) => {
      clearTimeout(timer)
      timer = setTimeout(() => {
        func.apply(this, args)
      }, timeout)
    }
  }

  function parseSellingPlan(el) {
    try {
      if (!el) {
        return null
      }
      return JSON.parse(el.innerHTML)
    } catch (error) {
      console.error(error)
      return null
    }
  }

  function parseImpactSaving(el) {
    try {
      const variants = {}
      const items = JSON.parse(el.innerHTML)
      items.forEach((item) => {
        try {
          let water = 0
          if (item.waterSaving.length > 0) {
            water = parseFloat(item.waterSaving)
          }
          let co2 = 0
          if (item.co2Saving.length > 0) {
            co2 = parseFloat(item.co2Saving)
          }

          variants[item.variantId] = {
            co2,
            water
          }
        } catch (error) {}
      })
      return variants
    } catch (error) {
      console.error(error)
      return {}
    }
  }

  function getCartItemBundleId(item) {
    const keys = Object.keys(item.properties || {})
    for (let index = 0; index < keys.length; index++) {
      const property = keys[index]
      const match = property.match(/^_bundler_(.+)$/)
      if (match) {
        return match[1]
      }
    }
  }

  function getVariantTitleAndSubtitle(item) {
    const [title, ...subtitle] = item.product_title.split(' ')
    return [
      title + (item.variant_title ? ` (${item.variant_title})` : ''),
      subtitle.join(' ')
    ]
  }

  function groupItems(items) {
    const groupedItems = []
    items.forEach((item) => {
      const bundleId = getCartItemBundleId(item)
      if (bundleId) {
        // part of a bundle
        let bundle = groupedItems.find((item) => item.bundleId === bundleId)
        if (!bundle) {
          bundle = {
            bundleId,
            items: []
          }
          groupedItems.push(bundle)
        }
        bundle.items.push(item)
      } else {
        groupedItems.push(item)
      }
    })
    return groupedItems
  }

  function getBundleQuantityInCart(bundleCartItem, bundleData) {
    const firstVariant = bundleCartItem.items[0]
    const bundleProduct = bundleData.products[String(firstVariant.id)]
    const bundleProductQuantity = bundleProduct ? bundleProduct.quantity : 1
    return firstVariant.quantity / bundleProductQuantity
  }

  function getItemSellingPlan(item) {
    return item.selling_plan_allocation &&
      item.selling_plan_allocation.selling_plan
      ? item.selling_plan_allocation.selling_plan.id
      : null
  }
  function getSubscriptionStatus(sellingPlan, cart) {
    let canActivate = true
    let active = true
    let quantity = 0

    if (cart.items.length === 0) {
      return {
        canActivate: false,
        active: false
      }
    }

    for (let index = 0; index < cart.items.length; index++) {
      const item = cart.items[index]
      const bundleId = getCartItemBundleId(item)
      if (bundleId) {
        // bundles can't have subscriptions
        canActivate = false
        active = false
        break
      } else {
        if (!sellingPlan.variantIds.includes(String(item.variant_id))) {
          // this variant can't have subscriptions
          canActivate = false
          active = false
          break
        } else {
          quantity += item.quantity
          if (getItemSellingPlan(item) !== sellingPlan.data.id) {
            active = false
          }
        }
      }
    }
    if (canActivate && quantity < 5) {
      // Can't subscribe when there are less than 5 items (business rule)
      canActivate = false
    }
    return {
      canActivate,
      active
    }
  }

  async function replaceCartItems(items) {
    await fetch(window.Shopify.routes.root + 'cart/clear.js', {
      method: 'POST'
    })
    await fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        items
      })
    })

    // fetch updated cart
    const res = await fetch(window.Shopify.routes.root + 'cart.js')
    const cart = await res.json()
    return cart
  }

  // from 15.90 to 1590
  function floatPriceToInteger(floatPrice) {
    return Math.round(floatPrice * 100)
  }

  // return the price in integers, e.g. 1590 for $15.90
  function getBundleFinalPrice(bundle, totalPrice) {
    if (bundle.discount_type === 'percentage') {
      const percentage = parseInt(bundle.percentage_value, 10)
      const bundlePrice = totalPrice - (totalPrice / 100) * percentage
      return bundlePrice
    } else if (bundle.discount_type === 'fixed_amount') {
      return totalPrice - floatPriceToInteger(bundle.fixed_amount_value)
    } else if (bundle.discount_type === 'fixed_price') {
      return floatPriceToInteger(bundle.fixed_price_value)
    } else if (bundle.discount_type === 'products_discounts') {
      let discount = 0
      Object.values(bundle.products).forEach((product) => {
        discount += floatPriceToInteger(product.discount_amount)
      })
      return totalPrice - discount
    }
    return totalPrice
  }

  class PlantedCart extends HTMLElement {
    connectedCallback() {
      const self = this
      let cartChanges
      // save the order of the items in the cart so that when we remove them
      // and re-add them with a selling plan (for the subscription) the order
      // doesn't change and the user doesn't see the products changing position
      let cart = JSON.parse(this.getAttribute('data-initial-cart'))
      let orderedVariantIds = cart.items.map((item) => item.variant_id)
      const freeShippingOboveAmount = parseInt(
        this.getAttribute('data-free-shipping-above') || 0,
        10
      )
      const freeShippingOboveAmountFormat = this.getAttribute(
        'data-free-shipping-format'
      )
      const shouldRenderOnLoad =
        this.getAttribute('data-render-onload') === 'true'
      const moneyFormat = this.getAttribute('data-money-format')
      const checkoutLinkEl = this.querySelector('a.js-checkout-link')
      const cartItemTemplate = this.querySelector('div[id="cart-item"]')
      const bundleCartItemTemplate = this.querySelector(
        'div[id="bundle-cart-item"]'
      )
      const cartItemsEl = self.querySelector('[data-cart-items]')
      const cartEmptyEl = self.querySelector('[data-cart-empty]')
      const impactSavingEls = self.querySelectorAll('[data-impact-saving-box]')
      const co2SavingEls = self.querySelectorAll('[data-co2-saving]')
      const waterSavingEls = self.querySelectorAll('[data-water-saving]')
      const impactSaving = parseImpactSaving(
        this.querySelector('script[data-impact-saving]')
      )
      const recommendedProductsEl = self.querySelector(
        '[data-cart-recommended-products]'
      )

      const sellingPlan = parseSellingPlan(
        self.querySelector('script[data-subscription-selling-plan]')
      )

      function disableCheckoutButton() {
        checkoutLinkEl.style.opacity = '0.3'
        checkoutLinkEl.style.pointerEvents = 'none'
      }

      function enableCheckoutButton() {
        checkoutLinkEl.style.opacity = ''
        checkoutLinkEl.style.pointerEvents = ''
      }

      async function removeSubscription() {
        // remove subscription
        disableCheckoutButton()
        try {
          const itemsWithoutSellingPlan = []
          cart.items.forEach((item) => {
            itemsWithoutSellingPlan.push({
              ...item,
              selling_plan: null
            })
          })
          // We can't buld edit selling_plan yet, hence we need to remove
          // and add the items again...
          cart = await replaceCartItems(itemsWithoutSellingPlan)
          const customEvent = new CustomEvent('theme:cart_updated', {
            detail: {
              cart
            }
          })
          document.dispatchEvent(customEvent)
        } catch (error) {
          console.error(error)
        }
        enableCheckoutButton()
        renderCart()
      }

      const cartSubscribeToggle = self.querySelector('#cart_subscribe_toggle')
      if (sellingPlan && cartSubscribeToggle) {
        cartSubscribeToggle.addEventListener('change', async () => {
          if (cartSubscribeToggle.checked) {
            // add subscription
            const itemsWithSellingPlan = []
            cart.items.forEach((item) => {
              itemsWithSellingPlan.push({
                ...item,
                selling_plan: sellingPlan.data.id
              })
            })
            disableCheckoutButton()

            try {
              // We can't buld edit selling_plan yet, hence we need to remove
              // and add the items again...
              cart = await replaceCartItems(itemsWithSellingPlan)

              cart = json
              const customEvent = new CustomEvent('theme:cart_updated', {
                detail: {
                  cart
                }
              })
              document.dispatchEvent(customEvent)
            } catch (error) {
              console.error(error)
            }
            enableCheckoutButton()
            renderCart()
          } else {
            await removeSubscription()
          }
        })
      }

      recommendedProductsEl.addEventListener('click', function (event) {
        const el = event.target.closest('[data-variant-id]')
        if (el && !el.disabled) {
          const variantId = parseInt(el.getAttribute('data-variant-id'), 10)
          if (variantId) {
            let formData = {
              items: [
                {
                  id: variantId,
                  quantity: 1
                }
              ]
            }
            el.disabled = true
            fetch(window.Shopify.routes.root + 'cart/add.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify(formData)
            })
              .then(() => {
                fetch(window.Shopify.routes.root + 'cart.js')
                  .then((res) => res.json())
                  .then(async (newCart) => {
                    cart = newCart
                    await sanitizeCart(cart)
                    orderedVariantIds = [variantId, ...orderedVariantIds]
                    renderCart()
                  })
                  .catch(() => {
                    renderCart()
                  })
              })
              .catch(() => {
                renderCart()
              })
          }
        }
      })

      // listen to - and + buttons on items
      cartItemsEl.addEventListener('click', function (event) {
        const buttonEl = event.target.closest('button')
        if (!buttonEl) {
          return
        }

        const itemEl = buttonEl.closest('[data-item-key]')
        if (!itemEl) {
          return
        }

        const itemKey = itemEl.getAttribute('data-item-key')
        if (!itemKey) {
          return
        }

        const item = cart.items.find((item) => item.key === itemKey)
        if (!item) {
          return
        }

        const quantityEl = itemEl.querySelector('[data-cart-item-quantity]')
        if (!quantityEl) {
          return
        }

        const initialQuantity = parseInt(quantityEl.innerHTML, 10)
        let quantity = initialQuantity
        if (buttonEl.hasAttribute('data-decrease-quantity')) {
          quantity = Math.max(0, quantity - 1)
        } else if (buttonEl.hasAttribute('data-increase-quantity')) {
          quantity = quantity + 1
        }

        quantityEl.innerHTML = quantity
        item.quantity = quantity
        debounceUpdateCart({
          [item.key]: quantity
        })
      })

      // listen to - and + buttons on bundles
      cartItemsEl.addEventListener('click', function (event) {
        const buttonEl = event.target.closest('button')
        if (!buttonEl) {
          return
        }

        const bundleEl = buttonEl.closest('[data-bundle-id]')
        if (!bundleEl) {
          return
        }

        const bundleId = bundleEl.getAttribute('data-bundle-id')
        if (!bundleId) {
          return
        }

        const groupedItems = groupItems(cart.items)
        const item = groupedItems.find((item) => item.bundleId === bundleId)
        if (!item) {
          return
        }

        const quantityEl = bundleEl.querySelector('[data-cart-item-quantity]')
        if (!quantityEl) {
          return
        }

        const initialQuantity = parseInt(quantityEl.innerHTML, 10)
        let quantity = initialQuantity
        if (buttonEl.hasAttribute('data-decrease-quantity')) {
          quantity = Math.max(0, quantity - 1)
        } else if (buttonEl.hasAttribute('data-increase-quantity')) {
          quantity = quantity + 1
        }

        quantityEl.innerHTML = quantity
        const changes = {}
        item.items.forEach((bundleItem) => {
          const bundleItemQuantity = Math.round(
            (quantity / initialQuantity) * bundleItem.quantity
          )
          bundleItem.quantity = bundleItemQuantity
          changes[bundleItem.key] = bundleItemQuantity
        })

        debounceUpdateCart(changes)
      })

      async function updateCart(changes) {
        try {
          const res = await fetch(
            window.Shopify.routes.root + 'cart/update.js',
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                updates: changes
              })
            }
          )
          if (res.ok) {
            const json = await res.json()
            cart = json
            // do not recompute orderedVariantIds, otherwise decreasing the quantity from 1 to 0
            // on a product might change the order again
            const customEvent = new CustomEvent('theme:cart_updated', {
              detail: {
                cart
              }
            })
            document.dispatchEvent(customEvent)
            await sanitizeCart(cart)
          } else {
            console.error(res.status)
            console.error(await res.text())
          }
        } catch (error) {
          console.error(error)
        }

        renderCart()
      }

      const debounceUpdateCart = debounce(updateCart)
      function renderCart() {
        // sort the items (toggling on/off subscription can change the order...)
        cart.items.sort((a, b) => {
          return orderedVariantIds.indexOf(a.variant_id) <
            orderedVariantIds.indexOf(b.variant_id)
            ? -1
            : 1
        })

        // group bundles
        const groupedItems = groupItems(cart.items)

        // add cart items
        const variantIds = []
        cartItemsEl.innerHTML = ''
        let isAtLeastABundleInCart = false
        // total price is used only when there's at least a bundle
        // because the standard cart.total_price will be wrong.
        // In case of subscriptions (and thus no bundles)
        // cart.total_price would be preferred since it's more precise (e.g. rounding)
        let totalPrice = 0

        groupedItems.forEach((item) => {
          if (item.bundleId) {
            // display a bundle
            const itemEl =
              bundleCartItemTemplate.firstElementChild.cloneNode(true)
            itemEl.setAttribute('data-bundle-id', item.bundleId)

            let bundleData
            try {
              bundleData = bndlr
                .getBundles()
                .find((b) => String(b.id) === String(item.bundleId))
            } catch (error) {}

            const itemsContainerEl = itemEl.querySelector(
              '#bundle-cart-item-variants'
            )
            const variantTemplate = itemEl.querySelector(
              '#bundle-cart-item-variant'
            )
            item.items.forEach((variant) => {
              variantIds.push(String(item.variant_id))

              const variantEl =
                variantTemplate.firstElementChild.cloneNode(true)

              const [title, subtitle] = getVariantTitleAndSubtitle(variant)
              variantEl.querySelector('[data-cart-item-title]').innerHTML =
                title
              variantEl.querySelector('[data-cart-item-subtitle]').innerHTML =
                subtitle

              const productQuantity =
                bundleData && bundleData.products[variant.id]
                  ? bundleData.products[variant.id].quantity
                  : 1
              if (productQuantity > 1) {
                const quantityEl = variantEl.querySelector(
                  '[data-cart-item-variant-quantity]'
                )
                quantityEl.innerHTML = productQuantity
                quantityEl.style.display = ''
              }
              const imageEl = variantEl.querySelector(
                'img[data-cart-item-image]'
              )
              imageEl.setAttribute('alt', variant.featured_image.alt)
              imageEl.setAttribute('src', variant.featured_image.url)

              itemsContainerEl.appendChild(variantEl)
            })

            if (bundleData) {
              const quantity = getBundleQuantityInCart(item, bundleData)
              let price = 0
              item.items.forEach(function (bundleItem) {
                price +=
                  parseInt(bundleItem.price, 10) *
                  (bundleItem.quantity / quantity)
              })
              const formattedPrice = Shopify.formatMoney(
                price * quantity,
                moneyFormat
              )

              itemEl.querySelector('[data-cart-item-price]').innerHTML =
                formattedPrice

              const singleBundlePrice = getBundleFinalPrice(bundleData, price)
              const bundlePrice = singleBundlePrice * quantity
              totalPrice += bundlePrice
              isAtLeastABundleInCart = true
              const formattedBundlePrice = Shopify.formatMoney(
                bundlePrice,
                moneyFormat
              )
              itemEl.querySelector(
                '[data-cart-item-discount-price]'
              ).innerHTML = formattedBundlePrice

              itemEl.querySelector('[data-cart-item-quantity]').innerHTML =
                quantity
            }

            cartItemsEl.appendChild(itemEl)
          } else {
            // display a normal item
            const itemEl = cartItemTemplate.firstElementChild.cloneNode(true)
            variantIds.push(String(item.variant_id))
            itemEl.setAttribute('data-item-key', item.key)
            itemEl.querySelector('[data-cart-item-unit-price]').innerHTML =
              Shopify.formatMoney(item.final_line_price, moneyFormat)
            totalPrice += item.final_line_price
            itemEl.querySelector('[data-cart-item-quantity]').innerHTML =
              item.quantity

            const [title, subtitle] = getVariantTitleAndSubtitle(item)
            itemEl.querySelector('[data-cart-item-title]').innerHTML = title
            itemEl.querySelector('[data-cart-item-subtitle]').innerHTML =
              subtitle
            const imageEl = itemEl.querySelector('img[data-cart-item-image]')
            imageEl.setAttribute('alt', item.featured_image.alt)
            imageEl.setAttribute('src', item.featured_image.url)
            cartItemsEl.appendChild(itemEl)
          }
        })

        // subscription
        if (cartSubscribeToggle) {
          const { canActivate, active } = getSubscriptionStatus(
            sellingPlan,
            cart
          )
          cartSubscribeToggle.disabled = !canActivate
          cartSubscribeToggle.checked = active
        }

        // hide/show recommended products
        let visibleItems = 0
        recommendedProductsEl
          .querySelectorAll('[data-variant-id]')
          .forEach(function (el) {
            const variantId = el.getAttribute('data-variant-id')
            if (variantIds.includes(variantId) || visibleItems > 5) {
              el.style.display = 'none'
            } else {
              visibleItems++
              el.style.display = ''
              el.disabled = false
            }
          })

        if (window.recommendedProductsSwiper) {
          // we shown/hidden some products, hence we need to update the slider
          window.recommendedProductsSwiper.update()
        }

        // impact saving
        let water = 0
        let co2 = 0
        cart.items.forEach((item) => {
          const saving = impactSaving[item.id]
          if (saving) {
            water += saving.water * item.quantity
            co2 += saving.co2 * item.quantity
          }
        })
        if (water > 0 || co2 > 0) {
          waterSavingEls.forEach((el) => (el.innerHTML = water.toFixed(1)))
          co2SavingEls.forEach((el) => (el.innerHTML = co2.toFixed(1)))
          impactSavingEls.forEach((el) => (el.style.display = ''))
        } else {
          impactSavingEls.forEach((el) => (el.style.display = 'none'))
        }

        // "checkout =>" button
        if (cart.items.length === 0) {
          disableCheckoutButton()
          cartItemsEl.style.display = 'none'
          cartEmptyEl.style.display = 'block'
        } else {
          enableCheckoutButton()
          cartItemsEl.style.display = ''
          cartEmptyEl.style.display = 'none'
        }

        if (!isAtLeastABundleInCart) {
          // with bundles, cart.total_price would be wrong - without discount
          totalPrice = cart.total_price
        }
        totalPrice = parseInt(totalPrice, 10)
        if (Shopify.currency.active === 'CHF') {
          // round cents to 0 or 5.
          const rest = totalPrice % 5
          if (rest > 0) {
            totalPrice -= rest
          }
        }
        const formattedTotalPrice = Shopify.formatMoney(totalPrice, moneyFormat)
        self.querySelector('[data-sub-total]').innerHTML = formattedTotalPrice

        if (freeShippingOboveAmount) {
          const missingAmount = freeShippingOboveAmount - totalPrice
          if (missingAmount <= 0) {
            self.querySelector('[data-amount-free-shipping]').innerHTML = ''
            self.querySelector('[data-amount-free-shipping-bar]').style.width =
              '100%'
          } else {
            const text = freeShippingOboveAmountFormat.replace(
              '[amount]',
              Shopify.formatMoney(missingAmount, moneyFormat)
            )
            if (typeof Weglot !== 'undefined') {
              Weglot.translate(
                {
                  words: [{ t: 1, w: text }],
                  languageTo: Weglot.getCurrentLang()
                },
                function (data) {
                  self.querySelector('[data-amount-free-shipping]').innerHTML =
                    data[0]
                }
              )
            } else {
              self.querySelector('[data-amount-free-shipping]').innerHTML = text
            }

            self.querySelector(
              '[data-amount-free-shipping-bar]'
            ).style.width = `${
              100 - (missingAmount / freeShippingOboveAmount) * 100
            }%`
          }
        }
      }

      // remove active subscription if not "legal" (e.g. products not part of the subscription or less than 5 products)
      async function sanitizeCart(
        cart,
        validateAgainAfterSubscriptionRemoval = true
      ) {
        const isSubscriptionActive = !!cart.items.find(
          (item) => getItemSellingPlan(item) !== null
        )
        if (isSubscriptionActive) {
          const { canActivate } = getSubscriptionStatus(sellingPlan, cart)
          if (!canActivate) {
            // invalid status, remove subscription
            disableCheckoutButton()
            try {
              await removeSubscription()
              if (validateAgainAfterSubscriptionRemoval) {
                // in case we get back a cart from another window tab etc,
                // it should do nothing
                await sanitizeCart(cart, false)
              }
            } catch (error) {
              console.error(error)
            }
            enableCheckoutButton()
          }
        }
      }

      async function init() {
        if (Shopify.formatMoney === undefined) {
          // wait for Shopify to fully load
          return setTimeout(init, 150)
        }
        await sanitizeCart(cart)
        renderCart()
        if (!window.bndlr) {
          document.addEventListener('bundler:loaded', renderCart)
        }
      }

      if (shouldRenderOnLoad) {
        if (document.readyState === 'loading') {
          // Loading hasn't finished yet
          document.addEventListener('DOMContentLoaded', init)
        } else {
          // `DOMContentLoaded` has already fired
          init()
        }
      } else {
        document.addEventListener('theme:show_cart', async (event) => {
          if (event.detail) {
            if (event.detail.changes) {
              cartChanges = event.detail.changes
              self.querySelector('[data-product-added]').style.display = ''
              self.querySelector('[data-added-items]').innerHTML =
                cartChanges.quantity
            } else {
              self.querySelector('[data-product-added]').style.display = 'none'
            }
            if (event.detail.cart) {
              cart = event.detail.cart
              orderedVariantIds = cart.items.map((item) => item.variant_id)
              await sanitizeCart(cart)
            }
          } else {
            self.querySelector('[data-product-added]').style.display = 'none'
          }

          init()
        })
      }
    }
  }
  customElements.define('planted-cart', PlantedCart)
}
